import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Card,
  Button,
  Typography,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  App as AntApp,
  Popconfirm,
  Alert,
  Descriptions,
  Divider,
  Tooltip,
} from 'antd';
import {
  UserAddOutlined,
  ReloadOutlined,
  StopOutlined,
  CopyOutlined,
  LockOutlined,
  EditOutlined,
} from '@ant-design/icons';
import apiClient from '@/services/api';
import { grantSudo } from '@/services/session';

const { Title, Text, Paragraph } = Typography;

interface UserRow {
  idx: string;
  commonName: string | null;
  displayName: string | null;
  email: string | null;
  organizationType: string | null;
  vendorIdx: string | null;
  distributorIdx: string | null;
  role: string | null;
  enabled: boolean | null;
  lastLogin: string | null;
}

interface UsersResp {
  Result: string;
  count: number;
  users: UserRow[];
}

interface OrgResp {
  vendors: { idx: string; name: string }[];
  distributors: { idx: string; name: string }[];
}

interface CreateUserResp {
  Result: string;
  user: { idx: string; commonName: string; role: string; organizationType: string };
  p12Password: string;
  downloadUrl: string;
  downloadToken: string;
  expiresAt: string;
  cert: { serial: string; commonName: string; expiresAt: string };
}

interface ReissueResp extends Omit<CreateUserResp, 'user'> {
  user: { idx: string; commonName: string };
  revokedSerials: string[];
}

function roleOptions(orgType: 'system' | 'distributor' | 'vendor'): { label: string; value: string }[] {
  if (orgType === 'system')
    return [
      { label: 'system_super_admin', value: 'system_super_admin' },
      { label: 'system_ops_admin', value: 'system_ops_admin' },
    ];
  if (orgType === 'distributor')
    return [
      { label: 'distributor_admin', value: 'distributor_admin' },
      { label: 'distributor_viewer', value: 'distributor_viewer' },
    ];
  return [
    { label: 'vendor_operator', value: 'vendor_operator' },
    { label: 'vendor_viewer', value: 'vendor_viewer' },
  ];
}

function IssuedP12Info({
  data,
  onClose,
  title,
}: {
  data: CreateUserResp | ReissueResp;
  onClose: () => void;
  title: string;
}) {
  const { message } = AntApp.useApp();
  const copy = (v: string, label: string) => {
    void navigator.clipboard.writeText(v);
    message.success(`${label} copied`);
  };
  return (
    <Modal
      open
      onCancel={onClose}
      onOk={onClose}
      title={title}
      width={720}
      okText="Done"
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Deliver this via two separate channels"
        description={
          <>
            Send the <b>download URL</b> by one channel (e.g. email) and the
            <b> P12 password</b> by another (e.g. phone / SMS). This page will not show them again.
          </>
        }
      />
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="User">
          {(data as any).user?.commonName ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Cert serial">
          <code>{data.cert.serial}</code>
        </Descriptions.Item>
        <Descriptions.Item label="Expires">
          {new Date(data.cert.expiresAt).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label="Download URL">
          <Space>
            <Text copyable style={{ wordBreak: 'break-all' }}>
              {data.downloadUrl}
            </Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="P12 password">
          <Space>
            <Text code style={{ fontSize: 14 }}>
              {data.p12Password}
            </Text>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copy(data.p12Password, 'Password')}
            >
              Copy
            </Button>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Token expires">
          {new Date(data.expiresAt).toLocaleString()} (one-time use)
        </Descriptions.Item>
      </Descriptions>
      {'revokedSerials' in data && data.revokedSerials.length > 0 && (
        <>
          <Divider />
          <Paragraph>
            <Text type="secondary">Revoked previous serials:</Text>
            <br />
            {data.revokedSerials.map((s) => (
              <Tag key={s}>{s}</Tag>
            ))}
          </Paragraph>
        </>
      )}
    </Modal>
  );
}

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserRow | null>(null);
  const [sudoOpen, setSudoOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | { kind: 'create' | 'reissue' | 'revoke' | 'edit'; payload?: any }>(null);
  const [issueResult, setIssueResult] = useState<CreateUserResp | ReissueResp | null>(null);
  const [issueTitle, setIssueTitle] = useState('Issued P12');
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [sudoForm] = Form.useForm();
  const queryClient = useQueryClient();

  const users = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const r = await apiClient.get<UsersResp>('/admin/users');
      return r as unknown as UsersResp;
    },
  });

  const orgs = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: async () => {
      const r = await apiClient.get<OrgResp>('/admin/organizations');
      return r as unknown as OrgResp;
    },
  });

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const r = await apiClient.get<{ sudoActive: boolean; sudoUntil: string | null }>('/auth/session-me');
      return (r as any).data ?? r;
    },
  });
  const sudoActive = !!(meQuery.data as any)?.sudoActive;

  const ensureSudoThen = (action: { kind: 'create' | 'reissue' | 'revoke' | 'edit'; payload?: any }) => {
    if (sudoActive) return runAction(action);
    setPendingAction(action);
    setSudoOpen(true);
  };

  const grantSudoMut = useMutation({
    mutationFn: (password: string) => grantSudo(password),
    onSuccess: () => {
      message.success('Sudo granted for 15 minutes');
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      setSudoOpen(false);
      if (pendingAction) {
        const a = pendingAction;
        setPendingAction(null);
        setTimeout(() => runAction(a), 100);
      }
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Sudo failed');
    },
  });

  const createUserMut = useMutation({
    mutationFn: async (body: any) => {
      const r = await apiClient.post<CreateUserResp>('/admin/users', body);
      return r as unknown as CreateUserResp;
    },
    onSuccess: (data) => {
      message.success('User created');
      setCreateOpen(false);
      createForm.resetFields();
      setIssueResult(data);
      setIssueTitle('New P12 issued');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || String(e));
    },
  });

  const reissueMut = useMutation({
    mutationFn: async (userIdx: string) => {
      const r = await apiClient.post<ReissueResp>(`/admin/users/${userIdx}/reissue-p12`, {
        purpose: 'Reissued from admin UI',
      });
      return r as unknown as ReissueResp;
    },
    onSuccess: (data) => {
      message.success('P12 reissued, old certs revoked');
      setIssueResult(data);
      setIssueTitle('P12 reissued');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || String(e));
    },
  });

  const editUserMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: any }) => {
      return apiClient.patch(`/admin/users/${id}`, body);
    },
    onSuccess: () => {
      message.success('User updated');
      setEditTarget(null);
      editForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const revokeMut = useMutation({
    mutationFn: async ({ userIdx, disableUser }: { userIdx: string; disableUser: boolean }) => {
      await apiClient.post(`/admin/users/${userIdx}/revoke-p12`, {
        reason: 'admin_revoke',
        disableUser,
      });
    },
    onSuccess: () => {
      message.success('P12 revoked');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || String(e));
    },
  });

  const runAction = (a: { kind: 'create' | 'reissue' | 'revoke' | 'edit'; payload?: any }) => {
    if (a.kind === 'create') createUserMut.mutate(a.payload);
    else if (a.kind === 'reissue') reissueMut.mutate(a.payload);
    else if (a.kind === 'revoke') revokeMut.mutate(a.payload);
    else if (a.kind === 'edit') editUserMut.mutate(a.payload);
  };

  const onCreateSubmit = (values: any) => {
    const payload: any = {
      commonName: values.commonName,
      displayName: values.displayName,
      email: values.email,
      organizationType: values.organizationType,
      role: values.role,
    };
    if (values.organizationType === 'vendor') payload.vendorIdx = values.vendorIdx;
    if (values.organizationType === 'distributor') payload.distributorIdx = values.distributorIdx;
    if (values.organizationType === 'system') payload.sudoPassword = values.sudoPassword;
    ensureSudoThen({ kind: 'create', payload });
  };

  const orgType = Form.useWatch('organizationType', createForm) as 'system' | 'distributor' | 'vendor' | undefined;

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          {t('admin.usersTitle')}
        </Title>
        <Space>
          {!sudoActive && (
            <Tooltip title="Most actions require sudo">
              <Button icon={<LockOutlined />} onClick={() => setSudoOpen(true)}>
                {t('admin.enterSudo')}
              </Button>
            </Tooltip>
          )}
          {sudoActive && <Tag color="orange">SUDO active</Tag>}
          <Button type="primary" icon={<UserAddOutlined />} onClick={() => setCreateOpen(true)}>
            {t('admin.newUser')}
          </Button>
        </Space>
      </Space>

      <Card>
        <Table
          size="small"
          rowKey="idx"
          loading={users.isLoading}
          dataSource={users.data?.users ?? []}
          pagination={{ pageSize: 20 }}
          columns={[
            { title: 'CN', dataIndex: 'commonName', key: 'cn' },
            { title: 'Display', dataIndex: 'displayName', key: 'dn' },
            {
              title: 'Org type',
              dataIndex: 'organizationType',
              key: 'ot',
              width: 130,
              render: (v: string) => <Tag>{v ?? '—'}</Tag>,
            },
            {
              title: 'Role',
              dataIndex: 'role',
              key: 'role',
              width: 180,
              render: (v: string) => <code>{v ?? '—'}</code>,
            },
            {
              title: 'Enabled',
              dataIndex: 'enabled',
              key: 'en',
              width: 90,
              render: (v: boolean) => (v ? <Tag color="green">YES</Tag> : <Tag>NO</Tag>),
            },
            {
              title: 'Actions',
              key: 'act',
              width: 280,
              render: (_, rec) => (
                <Space size="small">
                  <Button size="small" icon={<EditOutlined />} onClick={() => {
                    setEditTarget(rec);
                    editForm.setFieldsValue({
                      displayName: rec.displayName,
                      email: rec.email,
                      role: rec.role,
                      enabled: rec.enabled,
                    });
                  }}>{t('admin.edit')}</Button>
                  <Popconfirm
                    title={t('admin.reissueConfirm')}
                    description="Revokes all active certs and issues a new one."
                    onConfirm={() => ensureSudoThen({ kind: 'reissue', payload: rec.idx })}
                  >
                    <Button size="small" icon={<ReloadOutlined />}>
                      {t('admin.reissue')}
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title={t('admin.revokeConfirm')}
                    onConfirm={() =>
                      ensureSudoThen({ kind: 'revoke', payload: { userIdx: rec.idx, disableUser: true } })
                    }
                  >
                    <Button size="small" danger icon={<StopOutlined />}>
                      {t('admin.revoke')}
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={t('admin.createUser')}
        open={createOpen}
        onOk={() => createForm.submit()}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={createUserMut.isPending}
        okText="Create"
        width={600}
      >
        <Form form={createForm} layout="vertical" onFinish={onCreateSubmit}>
          <Form.Item
            name="commonName"
            label="Common Name"
            rules={[
              { required: true, message: 'Required' },
              { pattern: /^[A-Za-z0-9._-]{2,64}$/, message: 'Allowed: A-Z a-z 0-9 . _ -' },
            ]}
            extra="Unique identifier; also used as P12 cert CN"
          >
            <Input placeholder="e.g. alex-admin" />
          </Form.Item>
          <Form.Item name="displayName" label="Display name">
            <Input placeholder="Alex (Admin)" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input placeholder="optional" />
          </Form.Item>
          <Form.Item name="organizationType" label="Organization type" rules={[{ required: true }]}>
            <Select
              options={[
                { label: 'System (our side)', value: 'system' },
                { label: 'Distributor', value: 'distributor' },
                { label: 'Vendor', value: 'vendor' },
              ]}
              onChange={() => createForm.setFieldsValue({ role: undefined, vendorIdx: undefined, distributorIdx: undefined })}
            />
          </Form.Item>
          {orgType === 'vendor' && (
            <Form.Item name="vendorIdx" label="Vendor" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={orgs.data?.vendors?.map((v) => ({ label: v.name, value: v.idx })) ?? []}
              />
            </Form.Item>
          )}
          {orgType === 'distributor' && (
            <Form.Item name="distributorIdx" label="Distributor" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={orgs.data?.distributors?.map((d) => ({ label: d.name, value: d.idx })) ?? []}
              />
            </Form.Item>
          )}
          {orgType && (
            <Form.Item name="role" label="Role" rules={[{ required: true }]}>
              <Select options={roleOptions(orgType)} />
            </Form.Item>
          )}
          {orgType === 'system' && (
            <Form.Item
              name="sudoPassword"
              label="Sudo password"
              rules={[{ required: true, min: 8, message: 'Required for system users (min 8 chars)' }]}
              extra="This password is used for elevated operations (sudo). Stored as bcrypt hash."
            >
              <Input.Password />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={t('admin.enterSudo')}
        open={sudoOpen}
        onCancel={() => {
          setSudoOpen(false);
          setPendingAction(null);
          sudoForm.resetFields();
        }}
        onOk={() => sudoForm.submit()}
        confirmLoading={grantSudoMut.isPending}
        okText="Grant"
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Re-enter your sudo password. Valid 15 minutes."
        />
        <Form
          form={sudoForm}
          layout="vertical"
          onFinish={(v) => grantSudoMut.mutate(v.password)}
        >
          <Form.Item name="password" label="Password" rules={[{ required: true }]}>
            <Input.Password autoFocus />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`${t('admin.editUser')}: ${editTarget?.commonName ?? ''}`}
        open={!!editTarget}
        onCancel={() => { setEditTarget(null); editForm.resetFields(); }}
        onOk={() => editForm.submit()}
        confirmLoading={editUserMut.isPending}
        okText="Save"
        width={500}
      >
        <Form form={editForm} layout="vertical" onFinish={(v) => {
          if (!editTarget) return;
          const body: any = {};
          if (typeof v.displayName === 'string') body.displayName = v.displayName;
          if (typeof v.email === 'string') body.email = v.email;
          if (typeof v.role === 'string') body.role = v.role;
          if (typeof v.enabled === 'boolean') body.enabled = v.enabled;
          if (v.sudoPassword && v.sudoPassword.length > 0) body.sudoPassword = v.sudoPassword;
          ensureSudoThen({ kind: 'edit', payload: { id: editTarget.idx, body } });
        }}>
          <Form.Item name="displayName" label="Display name">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Role">
            <Select options={
              editTarget?.organizationType === 'system'
                ? roleOptions('system')
                : editTarget?.organizationType === 'distributor'
                  ? roleOptions('distributor')
                  : roleOptions('vendor')
            } />
          </Form.Item>
          <Form.Item name="enabled" label="Enabled">
            <Select options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]} />
          </Form.Item>
          {editTarget?.organizationType === 'system' && (
            <Form.Item
              name="sudoPassword"
              label="Reset sudo password (leave blank to keep)"
              rules={[{ min: 8, message: 'Min 8 chars' }]}
            >
              <Input.Password placeholder="Leave blank to keep current" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {issueResult && (
        <IssuedP12Info
          data={issueResult}
          onClose={() => setIssueResult(null)}
          title={issueTitle}
        />
      )}
    </>
  );
}
